/**
 * 播放策略管理器
 * 负责策略的切换、事件管理和状态同步
 * 确保同一时刻只有一个播放策略在运行
 */
import { usePlaybackStore } from '@/store/playback.js';
import { strategyFactory } from './strategies/StrategyFactory.js';
import { VideoStrategy } from './strategies/VideoStrategy.js';
import { AudioStrategy } from './strategies/AudioStrategy.js';
import { LyricsStrategy } from './strategies/LyricsStrategy.js';
import { AlbumStrategy } from './strategies/AlbumStrategy.js';
import { watch } from 'vue';

function isTransientSwitchError(error) {
  if (!error) return false;
  if (error?.code === 'LOAD_SUPERSEDED') return true;
  if (error?.isTrusted === true) return true;
  if (error?.target?.tagName === 'AUDIO') return true;
  const name = error?.name || '';
  if (name === 'AbortError') return true;
  const message = error?.message || '';
  if (message.includes('LOAD_SUPERSEDED') || message.includes('aborted')) return true;
  return false;
}

export class PlaybackStrategyManager {
  constructor() {
    // 🔥 使用全局状态管理，而不是事件传递
    this.playbackStore = usePlaybackStore();
    
    // 当前策略实例
    this.currentStrategy = null;
    
    // 策略切换锁定（防止并发切换）
    this.isSwitching = false;
    // 🔥 新增：保存当前正在切换的歌曲ID，用于判断是否需要强制中断
    this.switchingSongId = null;
    
    // 🔥 自动播放标志：标记用户是否主动点击了新歌曲，应该在加载完成后自动播放
    this.shouldAutoPlay = false;
    
    // 随机播放历史记录（记录已播放的歌曲索引）
    this.randomPlayHistory = [];
    
    // 🔥 防止循环更新的标志：当播放器状态变化更新Store时，不应该再次触发播放器操作
    this.isInternalStateUpdate = false;
    // 播完时浏览器会先发 pause，单曲循环随后又 play。这条定时器若没取消，会把已经重新开播的按钮打成暂停。
    this._pauseConfirmTimer = null;
    
    // 🔥 新增：保存上一个策略的全屏状态
    this.lastFullscreenState = null;
    this.performanceTrace = null;
    
    /**
     * 获取上一个策略的全屏状态
     * @returns {Object|null} 包含 {isFullscreen: boolean, strategyType: string} 的对象
     */
    this.getLastFullscreenState = () => {
      return this.lastFullscreenState;
    };
    
    // 监听播放模式变化事件，清空随机播放历史
    this.setupPlayModeChangeListener();
    
    // 🔥 新增：监听 PlaybackStore 的播放状态变化，实现双向绑定
    this.setupPlaybackStateWatcher();
    this.setupCurrentTimeWatcher(); // 🔥 新增：监听播放时间变化
    
    console.log('🎵 [PlaybackStrategyManager] 策略管理器初始化完成');
  }

  getNowMs() {
    if (typeof performance !== 'undefined' && typeof performance.now === 'function') {
      return performance.now();
    }
    return Date.now();
  }

  createPerformanceTrace(song) {
    const t0 = this.getNowMs();
    const traceId = `album-${song?.id || 'unknown'}-${Date.now()}`;
    this.performanceTrace = {
      id: traceId,
      songId: song?.id || null,
      title: song?.title || '',
      t0,
      firstAlbumMediaChangedMarked: false
    };
    this.markPerformanceTrace('switchStrategy:start', {
      songId: song?.id || null,
      title: song?.title || ''
    });
  }

  getPerformanceTrace() {
    return this.performanceTrace;
  }

  markPerformanceTrace(step, extra = {}) {
    if (!this.performanceTrace) {
      return;
    }
    const elapsed = Math.round(this.getNowMs() - this.performanceTrace.t0);
    console.log(`[PERF][StrategyManager][${this.performanceTrace.id}] t+${elapsed}ms ${step}`, extra);
  }
  
  /**
   * 设置播放模式变化监听器
   */
  setupPlayModeChangeListener() {
    window.addEventListener('playback-mode-changed', (event) => {
      const { mode } = event.detail;
      console.log('🎵 [PlaybackStrategyManager] 播放模式变化:', mode);
      
      // 切换播放模式时清空随机播放历史
      if (mode !== 'random') {
        this.clearRandomPlayHistory();
      } else {
        // 切换到随机播放模式时也清空历史，重新开始
        this.clearRandomPlayHistory();
      }
    });
  }
  
  /**
   * 🔥 新增：设置播放状态监听器，实现双向绑定
   * 当用户点击UI按钮更新Store状态时，自动触发播放器操作
   */
  setupPlaybackStateWatcher() {
    // 使用 Vue 的 watch 监听 Store 状态变化
    watch(
      () => this.playbackStore.isPlaying,
      async (newValue, oldValue) => {
        // 如果是因为内部更新（播放器状态变化）导致的，不响应
        if (this.isInternalStateUpdate) {
          console.log('🎵 [PlaybackStrategyManager] 跳过内部状态更新，避免循环');
          this.isInternalStateUpdate = false;
          return;
        }

        if (newValue === oldValue) {
          return;
        }
        
        // 如果没有当前策略，无法执行操作
        if (!this.currentStrategy) {
          console.warn('⚠️ [PlaybackStrategyManager] 没有当前策略，无法响应状态变化');
          return;
        }
        
        console.log('🎵 [PlaybackStrategyManager] Store播放状态变化，自动触发播放器操作:', {
          oldValue,
          newValue
        });
        
        try {
          if (newValue) {
            // Store状态变为true，开始播放
            console.log('🎵 [PlaybackStrategyManager] Store状态为true，开始播放');
            await this.play();
          } else {
            // Store状态变为false，暂停播放
            console.log('🎵 [PlaybackStrategyManager] Store状态为false，暂停播放');
            this.pause();
          }
        } catch (error) {
          console.error('❌ [PlaybackStrategyManager] 响应Store状态变化失败:', error);
          // 如果操作失败，回退状态
          this.isInternalStateUpdate = true;
          this.playbackStore.setPlaying(oldValue);
        }
      },
      { immediate: false } // 不立即执行，避免初始化时触发
    );
  }
  
  /**
   * 🔥 新增：设置播放时间监听器
   * 当用户拖拽进度条更新 playbackStore.currentTime 时，自动设置音频/视频的实际时间
   */
  setupCurrentTimeWatcher() {
    // 用于跟踪 Store 时间是否是从策略的 timeupdate 事件更新的
    // 如果 true，说明是策略更新，不需要反向设置策略
    let isFromStrategyUpdate = false;
    
    watch(
      () => this.playbackStore.currentTime,
      (newTime, oldTime) => {
        // 如果没有当前策略，无法设置时间
        if (!this.currentStrategy) {
          return;
        }
        
        // 如果时间没有变化或无效，不需要操作
        if (newTime === oldTime || !isFinite(newTime) || isNaN(newTime)) {
          return;
        }
        
        // 🔥 关键判断：如果是从策略的 timeupdate 事件更新的，不反向设置（避免循环）
        if (isFromStrategyUpdate) {
          isFromStrategyUpdate = false; // 重置标志
          return;
        }
        
        // 用户拖拽或外部设置，需要同步到策略
        const timeDiff = Math.abs(newTime - oldTime);
        if (timeDiff > 0.5) { // 只有时间变化超过0.5秒才认为是用户拖拽
          console.log('🎯 [PlaybackStrategyManager] 检测到用户设置时间，同步到策略:', {
            storeTime: newTime.toFixed(1),
            oldTime: oldTime.toFixed(1),
            diff: timeDiff.toFixed(1)
          });
          
          try {
            this.currentStrategy.setCurrentTime(newTime);
          } catch (error) {
            console.error('❌ [PlaybackStrategyManager] 设置策略时间失败:', error);
          }
        }
      },
      { immediate: false }
    );
    
    // 🔥 保存 isFromStrategyUpdate 的引用，供 bindStrategyEvents 使用
    this._isFromStrategyUpdate = () => isFromStrategyUpdate;
    this._setFromStrategyUpdate = (value) => {
      isFromStrategyUpdate = value;
    };
  }
  
  /**
   * 切换播放策略（根据歌曲类型）
   * @param {Object} song - 歌曲对象
   * @returns {Promise<void>}
   */
  async switchStrategy(song) {
    const newSongId = song?.id;

    // 平台歌曲的播放地址会过期：超过一分钟后重新取，不要复用队列里上一轮留下的地址
    if (song?._needsFreshUrl) {
      const issuedAt = Number(song._urlIssuedAt || 0);
      if (!issuedAt || Date.now() - issuedAt > 60_000) {
        delete song.playUrl;
        delete song.streamUrl;
        delete song.url;
        delete song.audioUrl;
      }
    }

    // 🔥 修复：如果正在切换不同的歌曲，强制中断旧的切换流程
    if (this.isSwitching) {
      if (this.switchingSongId && this.switchingSongId !== newSongId) {
        console.warn('⚠️ [PlaybackStrategyManager] 检测到不同歌曲的切换请求，强制中断旧的切换流程', {
          oldSongId: this.switchingSongId,
          newSongId: newSongId
        });
        
        // 🔥 强制取消旧策略的加载操作
        try {
          this.cancelCurrentStrategyLoading();
        } catch (error) {
          console.warn('⚠️ [PlaybackStrategyManager] 强制取消旧策略时出错（继续切换）:', error);
        }
        
        // 🔥 重置切换标志，允许新切换继续
        this.isSwitching = false;
        this.switchingSongId = null;
      } else {
        console.warn('⚠️ [PlaybackStrategyManager] 策略正在切换中，忽略重复请求', {
          switchingSongId: this.switchingSongId,
          newSongId: newSongId
        });
        return;
      }
    }
    
    this.isSwitching = true;
    this.switchingSongId = newSongId; // 🔥 保存当前正在切换的歌曲ID
    
    try {
      this.createPerformanceTrace(song);
      console.log('🔄 [PlaybackStrategyManager] 开始切换策略，歌曲:', song?.title);
      
      // 🔥 修复：在切换策略开始时立即清除时长，防止旧歌曲的时长残留
      this.playbackStore.setDuration(0);
      console.log('🧹 [PlaybackStrategyManager] 已清除时长，防止旧歌曲时长残留');
      
      // 1. 清理旧策略（如果存在）
      if (this.currentStrategy) {
        console.log('🧹 [PlaybackStrategyManager] 开始清理旧策略');
        
        // 🔥 新增：立即取消旧策略的加载操作
        try {
          console.log('🚫 [PlaybackStrategyManager] 取消旧策略的加载操作');
          this.cancelCurrentStrategyLoading();
        } catch (error) {
          console.warn('⚠️ [PlaybackStrategyManager] 取消旧策略加载时出错（继续清理）:', error);
        }
        
        // 🔥 新增：在清理前保存旧策略的全屏状态
        if (this.currentStrategy.isInFullscreen && typeof this.currentStrategy.isInFullscreen === 'function') {
          const wasFullscreen = this.currentStrategy.isInFullscreen();
          this.lastFullscreenState = {
            isFullscreen: wasFullscreen,
            strategyType: this.getStrategyType(this.currentStrategy.constructor)
          };
          console.log('🎬 [PlaybackStrategyManager] 保存旧策略全屏状态:', this.lastFullscreenState);
        } else {
          // 如果没有全屏方法，清空状态
          this.lastFullscreenState = null;
        }
        
        // 停止播放
        try {
          this.currentStrategy.stop();
        } catch (error) {
          console.error('❌ [PlaybackStrategyManager] 停止旧策略失败:', error);
        }
        
        // 清理资源
        try {
          await this.currentStrategy.cleanup();
        } catch (error) {
          console.error('❌ [PlaybackStrategyManager] 清理旧策略失败:', error);
        }
        
        // 等待清理完成（确保资源完全释放）
        await this.waitForCleanupComplete();
        
        // 清空引用
        this.currentStrategy = null;
        
        console.log('✅ [PlaybackStrategyManager] 旧策略清理完成');
      } else {
        // 如果没有旧策略，清空全屏状态
        this.lastFullscreenState = null;
      }
      
      // 2. 根据歌曲类型选择策略
      const StrategyClass = strategyFactory.getStrategy(song);
      console.log('🔍 [PlaybackStrategyManager] 策略工厂返回的策略类:', {
        StrategyClass,
        className: StrategyClass?.name,
        isVideoStrategy: StrategyClass?.name === 'VideoStrategy',
        songInfo: {
          id: song?.id,
          videoId: song?.videoId,
          title: song?.title
        }
      });
      
      const strategyType = this.getStrategyType(StrategyClass);
      
      console.log('🆕 [PlaybackStrategyManager] 创建新策略，类型:', strategyType, '策略类名:', StrategyClass?.name);
      
      // 🔥 修复：如果新旧策略类型一致（例如图集 -> 图集、视频 -> 视频），强制触发一次策略类型变更
      // 这样可以通知前端组件（尤其是图集播放器）清理旧的 Blob URL 和状态，避免继续使用已被 revoke 的资源
      const previousStrategyType = this.playbackStore.currentStrategyType;
      if (previousStrategyType && previousStrategyType === strategyType) {
        console.log('🔁 [PlaybackStrategyManager] 新旧策略类型相同，先暂时重置策略类型为 null，确保前端能收到变更事件');
        this.playbackStore.setCurrentStrategyType(null);
      }
      
      // 🔥 修复：在策略选择后立即更新策略类型，让UI能及时响应
      // 这样 DXHPlayer 中的 isVideoMode 能在策略加载前就更新
      this.playbackStore.setCurrentStrategyType(strategyType);
      this.playbackStore.setCurrentSong(song);
      
      // 🔥 新增：保存当前歌曲ID，用于验证事件是否来自当前歌曲
      const currentSongIdForThisStrategy = song?.id;
      
      // 3. 创建新策略实例
      this.currentStrategy = new StrategyClass();
      this.markPerformanceTrace('strategy:instance-created', {
        strategy: this.currentStrategy?.constructor?.name || 'unknown'
      });
      if (this.currentStrategy && typeof this.currentStrategy.setPerformanceTraceContext === 'function') {
        this.currentStrategy.setPerformanceTraceContext(this.getPerformanceTrace());
        this.markPerformanceTrace('strategy:trace-bound', {
          strategy: this.currentStrategy?.constructor?.name || 'unknown'
        });
      }
      
      // 4. 绑定事件（更新全局状态，而不是事件传递）
      // 🔥 传递当前歌曲ID，用于验证事件是否匹配
      this.bindStrategyEvents(this.currentStrategy, currentSongIdForThisStrategy);
      
      // 5. 加载歌曲
      this.markPerformanceTrace('strategy.load:start', {});
      await this.currentStrategy.load(song);
      this.markPerformanceTrace('strategy.load:done', {});
      
      // 🔥 新增：如果上一个策略处于全屏状态，检查新策略是否需要自动进入全屏
      let wasPreviousStrategyFullscreen = false;
      // 注意：此时旧策略已经被清理，无法直接检查
      // 我们需要在清理前保存全屏状态，或者通过策略管理器保存
      if (this.lastFullscreenState) {
        wasPreviousStrategyFullscreen = this.lastFullscreenState.isFullscreen;
        console.log('🎬 [PlaybackStrategyManager] 上一个策略处于全屏状态，准备自动进入新策略的全屏模式');
      }
      
      // 6. 更新其他全局状态
      // 🔥 关键修复：不再在策略切换完成后立即检查 shouldAutoPlay 并设置播放状态
      // 这是因为：
      // 1. 如果 shouldAutoPlay 为 true（用户主动点击或自动切换），会在 canplay 事件中自动播放
      // 2. 如果 shouldAutoPlay 为 false（用户手动切换但不想播放），保持当前状态即可
      // 3. 过早设置播放状态为 false 会导致自动切换时出现播放/暂停循环
      console.log('🎵 [PlaybackStrategyManager] 策略切换完成，自动播放标志状态:', {
        shouldAutoPlay: this.shouldAutoPlay,
        currentStorePlaying: this.playbackStore.isPlaying,
        strategyType: strategyType
      });
      
      // 🔥 修复：load 期间可能已通过 canplay 开始播放（缓存命中时很快）。
      // 此时不应再把进度重置为 0，否则会触发 time watcher 将音频 seek 回开头。
      const strategyTime = typeof this.currentStrategy.getCurrentTime === 'function'
        ? this.currentStrategy.getCurrentTime()
        : 0;
      const storeTime = this.playbackStore.currentTime;

      if (strategyTime > 0.15 || storeTime > 0.15) {
        console.log('🎵 [PlaybackStrategyManager] load 完成时播放已开始，保持当前进度:', {
          strategyTime: strategyTime.toFixed(2),
          storeTime: storeTime.toFixed(2),
        });
        if (Math.abs(strategyTime - storeTime) > 0.3) {
          this._setFromStrategyUpdate?.(true);
          this.playbackStore.setCurrentTime(strategyTime);
        }
      } else {
        this.playbackStore.setCurrentTime(0);
      }
      
      // 🔥 优化：如果上一个策略处于全屏状态，DXHPlayer 已经在设置全屏控制接口后立即恢复了全屏状态
      // 这里不再需要延迟调用 enterFullscreenIfNeeded，因为全屏状态已经在 DXHPlayer 中处理了
      // 但为了兼容性，如果 DXHPlayer 没有处理（比如某些特殊情况），这里仍然作为备用方案
      if (wasPreviousStrategyFullscreen && this.currentStrategy.enterFullscreenIfNeeded) {
        // 🔥 优化：减少延迟时间，DXHPlayer 已经立即处理了全屏状态，这里只需要很小的延迟作为备用
        const strategyType = this.getStrategyType(StrategyClass);
        const delay = 50; // 只延迟50ms，作为备用方案
        
        // 等待一小段时间，确保 DXHPlayer 已经处理完全屏状态
        // 如果 DXHPlayer 已经处理了，这里的调用会被忽略（因为已经是全屏状态）
        const enterFullscreen = async () => {
          try {
            await new Promise(resolve => setTimeout(resolve, delay));
            // 检查是否已经处于全屏状态（DXHPlayer 可能已经处理了）
            if (this.currentStrategy.isInFullscreen && this.currentStrategy.isInFullscreen()) {
              console.log('🎬 [PlaybackStrategyManager] 全屏状态已在 DXHPlayer 中处理，跳过重复调用');
              return;
            }
            await this.currentStrategy.enterFullscreenIfNeeded();
            console.log('🎬 [PlaybackStrategyManager] 已自动进入新策略的全屏模式（备用方案）');
          } catch (error) {
            console.error('❌ [PlaybackStrategyManager] 自动进入全屏模式失败:', error);
          }
        };
        
        enterFullscreen();
      }
      
      // 7. 更新随机播放历史（如果当前歌曲不在历史中，添加到历史）
      const playlist = this.playbackStore.playlist;
      const currentIndex = this.playbackStore.currentSongIndex;
      if (playlist && playlist.length > 0 && currentIndex !== -1 && currentIndex < playlist.length) {
        // 在随机播放模式下，记录当前歌曲
        if (this.playbackStore.playMode === 'random' && !this.randomPlayHistory.includes(currentIndex)) {
          this.randomPlayHistory.push(currentIndex);
          console.log('🎲 [PlaybackStrategyManager] 更新随机播放历史:', this.randomPlayHistory);
        }
      }
      
      console.log('✅ [PlaybackStrategyManager] 策略切换完成，类型:', strategyType);
      this.markPerformanceTrace('switchStrategy:done', { strategyType });
      
    } catch (error) {
      if (isTransientSwitchError(error)) {
        console.warn('⚠️ [PlaybackStrategyManager] 忽略切歌过程中的瞬时播放错误:', error?.message || error);
        return;
      }
      console.error('❌ [PlaybackStrategyManager] 策略切换失败:', error);
      this.playbackStore.setError(error);
      throw error;
    } finally {
      this.isSwitching = false;
      this.switchingSongId = null; // 🔥 清除正在切换的歌曲ID
    }
  }
  
  /**
   * 获取策略类型字符串
   * @param {Function} StrategyClass - 策略类
   * @returns {string} 'audio' | 'video' | 'lyrics' | 'album'
   */
  getStrategyType(StrategyClass) {
    // 🔥 修复：使用直接比较而不是类名匹配，避免代码压缩导致的问题
    const className = StrategyClass.name;
    console.log('🔍 [PlaybackStrategyManager] getStrategyType 调试:', {
      className,
      StrategyClass,
      isVideoStrategy: StrategyClass === VideoStrategy,
      isAudioStrategy: StrategyClass === AudioStrategy,
      isLyricsStrategy: StrategyClass === LyricsStrategy,
      isAlbumStrategy: StrategyClass === AlbumStrategy
    });
    
    // 优先使用直接比较（更可靠，不受代码压缩影响）
    if (StrategyClass === VideoStrategy) {
      console.log('✅ [PlaybackStrategyManager] 识别为视频策略（直接比较）');
      return 'video';
    }
    if (StrategyClass === AlbumStrategy) {
      console.log('✅ [PlaybackStrategyManager] 识别为图集策略（直接比较）');
      return 'album';
    }
    if (StrategyClass === LyricsStrategy) {
      console.log('✅ [PlaybackStrategyManager] 识别为歌词策略（直接比较）');
      return 'lyrics';
    }
    if (StrategyClass === AudioStrategy) {
      console.log('✅ [PlaybackStrategyManager] 识别为音频策略（直接比较）');
      return 'audio';
    }
    
    // 备用方案：如果直接比较失败，使用类名匹配（兼容代码压缩的情况）
    if (className.includes('Video')) {
      console.log('✅ [PlaybackStrategyManager] 识别为视频策略（类名匹配）');
      return 'video';
    }
    if (className.includes('Album')) {
      console.log('✅ [PlaybackStrategyManager] 识别为图集策略（类名匹配）');
      return 'album';
    }
    if (className.includes('Lyrics')) {
      console.log('✅ [PlaybackStrategyManager] 识别为歌词策略（类名匹配）');
      return 'lyrics';
    }
    
    console.warn('⚠️ [PlaybackStrategyManager] 未匹配到策略类型，默认返回音频策略');
    return 'audio';
  }
  
  /**
   * 绑定策略事件，更新全局状态
   * @param {PlayStrategy} strategy - 策略实例
   * @param {number|string} expectedSongId - 期望的歌曲ID，用于验证事件是否来自当前歌曲
   */
  bindStrategyEvents(strategy, expectedSongId = null) {
    // 🔥 辅助函数：验证事件是否来自当前歌曲
    const isEventFromCurrentSong = () => {
      const currentSongId = this.playbackStore.currentSong?.id;
      if (expectedSongId == null) {
        // 未提供期望歌曲ID时，不拦截事件（避免误杀 canplay/playing）
        return true;
      }
      if (currentSongId == null) {
        return false;
      }
      // 统一按字符串比较，兼容 number/string ID 混用
      return String(currentSongId) === String(expectedSongId);
    };
    // 播放事件（调用 play() 后触发，但不一定已开始播放）
    strategy.on('play', () => {
      // 不在这里更新状态，等待 'playing' 事件
      console.log('🎵 [PlaybackStrategyManager] 播放方法已调用');
    });
    
    // 🔥 关键修复：使用 'playing' 事件来更新播放状态
    // 'playing' 事件表示音频真正开始播放，用户能听到声音
    // 这比 'play' 事件更准确
    strategy.on('playing', () => {
      // 单曲循环重新开播后，作废播完瞬间排队的 pause 确认
      this.clearPauseConfirmTimer();
      // 🔥 验证事件是否来自当前歌曲
      if (!isEventFromCurrentSong()) {
        console.log('🚫 [PlaybackStrategyManager] playing 事件来自旧歌曲，忽略', {
          expectedSongId,
          currentSongId: this.playbackStore.currentSong?.id
        });
        return;
      }
      
      console.log('🎵 [PlaybackStrategyManager] 🔥 收到策略的 playing 事件，音频真正开始播放，更新播放状态');
      console.log('🎵 [PlaybackStrategyManager] 当前 Store 状态:', {
        isPlaying: this.playbackStore.isPlaying,
        currentSong: this.playbackStore.currentSong?.title
      });
      if (strategy._userInitiatedPause) {
        console.log('🎵 [PlaybackStrategyManager] 用户已暂停，忽略 playing 事件并保持暂停');
        try {
          strategy.pause();
        } catch (error) {
          console.warn('⚠️ [PlaybackStrategyManager] 忽略 playing 后暂停失败:', error);
        }
        return;
      }
      // 已是播放态则不再写 Store，避免 isInternalStateUpdate 与用户 pause 同 tick 合并后吞掉 pause()
      if (this.playbackStore.isPlaying) {
        return;
      }
      // 🔥 标记为内部更新，避免触发状态监听器的循环
      this.isInternalStateUpdate = true;
      this.playbackStore.setPlaying(true);
      console.log('🎵 [PlaybackStrategyManager] ✅ Store 播放状态已更新为 true');
    });
    
    // 暂停事件
    strategy.on('pause', () => {
      console.log('🎵 [PlaybackStrategyManager] 音频暂停，更新播放状态');
      if (!this.playbackStore.isPlaying) {
        return;
      }
      if (strategy._userInitiatedPause) {
        this.clearPauseConfirmTimer();
        this.isInternalStateUpdate = true;
        this.playbackStore.setPlaying(false);
        return;
      }
      // iOS 切后台可能先 pause 音频元素，再把页面 hidden。延迟确认，避免误把后台播放打成暂停。
      this.clearPauseConfirmTimer();
      this._pauseConfirmTimer = window.setTimeout(() => {
        this._pauseConfirmTimer = null;
        if (!this.playbackStore.isPlaying) return;
        if (typeof document !== 'undefined' && document.hidden) {
          console.log('🎵 [PlaybackStrategyManager] 忽略切后台导致的 pause 事件，尝试恢复播放');
          this.play().catch((error) => {
            console.warn('🎵 [PlaybackStrategyManager] 切后台后恢复播放失败:', error);
          });
          return;
        }
        // 播完触发的 pause 可能晚于单曲循环的重新 play。媒体已经在播就不要改按钮。
        if (typeof strategy.isPlaying === 'function' && strategy.isPlaying()) {
          console.log('🎵 [PlaybackStrategyManager] 忽略过期 pause：媒体已在播放');
          return;
        }
        this.isInternalStateUpdate = true;
        this.playbackStore.setPlaying(false);
      }, 80);
    });
    
    // 时间更新事件
    strategy.on('timeupdate', (data) => {
      // 🔥 验证事件是否来自当前歌曲
      if (!isEventFromCurrentSong()) {
        return; // 静默忽略旧歌曲的时间更新
      }
      
      // 🔥 关键修复：策略的 timeupdate 事件更新全局状态
      // 标记这是从策略更新来的，避免 setupCurrentTimeWatcher 反向设置（避免循环）
      if (this._setFromStrategyUpdate) {
        this._setFromStrategyUpdate(true);
      }
      this.playbackStore.setCurrentTime(data.currentTime);
      if (data.duration && Number.isFinite(data.duration)) {
        this.playbackStore.setDuration(data.duration);
      }
    });
    
    // 播放结束事件
    strategy.on('ended', () => {
      // 🔥 防护：旧策略的 ended 事件不应驱动自动下一曲
      if (!isEventFromCurrentSong()) {
        console.log('🚫 [PlaybackStrategyManager] ended 事件来自旧歌曲，忽略', {
          expectedSongId,
          currentSongId: this.playbackStore.currentSong?.id
        });
        return;
      }

      this.clearPauseConfirmTimer();
      console.log('🔁 [AUTO-NEXT TRACE] 策略 ended 事件触发', {
        strategyType: strategy?.constructor?.name,
        currentSongId: this.playbackStore.currentSong?.id,
        currentSongTitle: this.playbackStore.currentSong?.title,
        playMode: this.playbackStore.playMode,
        playlistLength: this.playbackStore.playlist?.length || 0,
        isPlaying: this.playbackStore.isPlaying,
        shouldAutoPlay: this.shouldAutoPlay,
        isSwitching: this.isSwitching
      });
      // 播放结束，自动播放下一首
      this.isInternalStateUpdate = true;
      this.playbackStore.setPlaying(false);
      // 🔥 移动端优化：立即开始加载下一首，尽量在用户手势上下文内完成
      // 使用 Promise.resolve().then() 来尝试保持用户手势上下文
      Promise.resolve().then(() => {
        this.handleAutoNext();
      });
    });
    
    // 错误事件
    strategy.on('error', (data) => {
      // 更新错误状态
      this.playbackStore.setError(data.error || new Error('播放错误'));
    });
    
    // 元数据加载完成事件
    strategy.on('loadedmetadata', (data) => {
      // 🔥 验证事件是否来自当前歌曲
      if (!isEventFromCurrentSong()) {
        console.log('🚫 [PlaybackStrategyManager] loadedmetadata 事件来自旧歌曲，忽略', {
          expectedSongId,
          currentSongId: this.playbackStore.currentSong?.id
        });
        return;
      }
      
      // 更新总时长
      if (data.duration) {
        this.playbackStore.setDuration(data.duration);
      }
    });
    
    // 可以播放事件
    strategy.on('canplay', async () => {
      // 🔥 验证事件是否来自当前歌曲
      if (!isEventFromCurrentSong()) {
        console.log('🚫 [PlaybackStrategyManager] canplay 事件来自旧歌曲，忽略', {
          expectedSongId,
          currentSongId: this.playbackStore.currentSong?.id
        });
        return;
      }
      
      // 注意：不要在这里用 playbackStore.isPlaying 拦截视频策略的 canplay。
      // 切歌时上一首的播放状态可能仍为 true，会导致新视频的 canplay 被跳过、play() 永不调用而卡在加载中。
      // re-buffer 的重复 canplay 已由 VideoController 的 _canplayEmittedForCurrentSong 在源头去重。
      console.log('🎵 [PlaybackStrategyManager] 🔥 canplay 事件触发，音频可以开始播放');
      this.markPerformanceTrace('audio:canplay', {});
      console.log('🎵 [PlaybackStrategyManager] 当前状态:', {
        shouldAutoPlay: this.shouldAutoPlay,
        currentStorePlaying: this.playbackStore.isPlaying,
        currentSong: this.playbackStore.currentSong?.title
      });
      
      // 🔥 修复：如果用户主动点击了新歌曲，在 canplay 事件时自动播放
      if (this.shouldAutoPlay) {
        console.log('🎵 [PlaybackStrategyManager] ✅ 用户主动点击新歌曲，自动开始播放');
        console.log('🎵 [PlaybackStrategyManager] 调用 strategy.play()，等待 playing 事件更新状态');
        this.shouldAutoPlay = false; // 清除标志，避免重复播放
        
        // 🔥 修复：不在这里设置播放状态，等待 'playing' 事件来更新
        // 这样确保只有在音频真正开始播放（用户能听到声音）时才更新UI
        try {
          this.markPerformanceTrace('strategy.play:invoke-from-canplay', {});
          await this.play();
          this.markPerformanceTrace('strategy.play:resolved-from-canplay', {});
          console.log('🎵 [PlaybackStrategyManager] strategy.play() 调用成功，等待 playing 事件');
          
          // 🔥 关键修复：如果 play() 成功但没有立即触发 playing 事件，等待一小段时间后检查
          // 有些情况下，音频可能已经处于播放状态，但 playing 事件没有立即触发
          await new Promise(resolve => setTimeout(resolve, 100));
          
          // 检查策略是否真的在播放（通过策略的 isPlaying 方法）
          if (this.currentStrategy && typeof this.currentStrategy.isPlaying === 'function') {
            const actuallyPlaying = this.currentStrategy.isPlaying();
            console.log('🎵 [PlaybackStrategyManager] 检查实际播放状态:', {
              actuallyPlaying,
              storePlaying: this.playbackStore.isPlaying
            });
            
            // 如果实际上在播放，但 Store 状态不是 true，手动更新
            if (actuallyPlaying && !this.playbackStore.isPlaying) {
              console.log('🎵 [PlaybackStrategyManager] ⚠️ 检测到实际播放但 Store 状态不正确，手动修复');
              this.isInternalStateUpdate = true;
              this.playbackStore.setPlaying(true);
            }
          }
        } catch (error) {
          console.warn('🎵 [PlaybackStrategyManager] ⚠️ 自动播放失败:', error);
          
          // 🔥 移动端优化：如果自动播放失败（通常是 NotAllowedError），尝试使用 Promise.resolve().then() 重新尝试
          // 这有时可以在某些浏览器中保持用户手势上下文
          if (error.name === 'NotAllowedError' || error.message?.includes('user gesture')) {
            console.log('🎵 [PlaybackStrategyManager] 🔄 检测到移动端自动播放限制，尝试使用 Promise.resolve().then() 重新播放');
            try {
              // 使用 Promise.resolve().then() 来尝试保持用户手势上下文
              await Promise.resolve().then(async () => {
                await new Promise(resolve => setTimeout(resolve, 50)); // 短暂延迟
                await this.play();
                console.log('🎵 [PlaybackStrategyManager] ✅ 使用 Promise.resolve().then() 重新播放成功');
              });
            } catch (retryError) {
              console.warn('🎵 [PlaybackStrategyManager] ⚠️ 重新播放也失败:', retryError);
              // 如果重新播放也失败，保持播放状态为 false
              // 用户需要手动点击播放按钮
            }
          } else {
            // 如果自动播放失败，保持播放状态为 false
            // 用户需要手动点击播放按钮
          }
        }
      } else {
        console.log('🎵 [PlaybackStrategyManager] ❌ shouldAutoPlay 为 false，不自动播放');
      }
    });
    
    // 视频URL更新事件（仅视频策略）
    strategy.on('videoUrlUpdated', (data) => {
      // 视频策略特有的事件，可以在这里处理
      console.log('🎬 [PlaybackStrategyManager] 视频URL更新:', data.videoUrl);
    });
    
    // 🔥 新增：图集媒体加载完成事件（仅图集策略）
    strategy.on('albumMediaLoaded', (data) => {
      console.log('🖼️ [PlaybackStrategyManager] 图集媒体加载完成:', data.mediaItems?.length, '项');
      this.markPerformanceTrace('album:media-loaded-event', {
        mediaCount: data.mediaItems?.length || 0
      });
      // 可以在这里更新全局状态，如果需要的话
    });
    
    // 🔥 新增：图集媒体切换事件（仅图集策略）
    strategy.on('albumMediaChanged', (data) => {
      console.log('🖼️ [PlaybackStrategyManager] 图集媒体切换:', {
        currentIndex: data.currentIndex,
        total: strategy.getAllMediaItems ? strategy.getAllMediaItems().length : 0,
        mediaType: data.currentMedia?.mediaType
      });
      if (this.performanceTrace && !this.performanceTrace.firstAlbumMediaChangedMarked) {
        this.performanceTrace.firstAlbumMediaChangedMarked = true;
        this.markPerformanceTrace('album:first-media-changed-event', {
          currentIndex: data.currentIndex,
          mediaType: data.currentMedia?.mediaType
        });
      }
      // 可以在这里更新全局状态，如果需要的话
    });
  }
  
  /**
   * 🔥 新增：取消当前策略的加载操作
   * 当用户切换歌曲时，立即终止正在进行的加载，避免资源浪费
   */
  cancelCurrentStrategyLoading() {
    if (!this.currentStrategy) {
      return;
    }
    
    try {
      // 根据策略类型取消加载
      const strategyType = this.getStrategyType(this.currentStrategy.constructor);
      console.log('🚫 [PlaybackStrategyManager] 取消加载操作，策略类型:', strategyType);
      
      if (strategyType === 'audio' || strategyType === 'lyrics') {
        // 音频策略：通过 AudioController 取消加载
        if (this.currentStrategy.audioController) {
          const audioController = this.currentStrategy.audioController;
          
          // 取消当前加载的请求
          if (audioController.currentLoadAbortController) {
            console.log('🚫 [PlaybackStrategyManager] 取消音频加载请求');
            audioController.currentLoadAbortController.abort();
            audioController.currentLoadAbortController = null;
          }
          
          // 取消下载请求
          if (audioController.currentDownloadAbortController) {
            console.log('🚫 [PlaybackStrategyManager] 取消音频下载请求');
            audioController.currentDownloadAbortController.abort();
            audioController.currentDownloadAbortController = null;
          }
          
          // 取消后台下载请求
          if (audioController.backgroundDownloadAbortController) {
            console.log('🚫 [PlaybackStrategyManager] 取消音频后台下载请求');
            audioController.backgroundDownloadAbortController.abort();
            audioController.backgroundDownloadAbortController = null;
          }
          
          // 清理定时器
          if (audioController.switchCancelTimeout) {
            clearTimeout(audioController.switchCancelTimeout);
            audioController.switchCancelTimeout = null;
          }
          
          // 停止音频元素加载（如果存在）
          if (audioController.audio) {
            try {
              audioController.audio.pause();
              audioController.audio.src = '';
              audioController.audio.load(); // 重置加载状态
              console.log('🚫 [PlaybackStrategyManager] 已停止音频元素加载');
            } catch (error) {
              console.warn('⚠️ [PlaybackStrategyManager] 停止音频元素时出错:', error);
            }
          }
        }
      } else if (strategyType === 'video') {
        // 视频策略：通过 VideoController 取消加载
        if (this.currentStrategy.videoController) {
          const videoController = this.currentStrategy.videoController;
          
          // 🔥 首先设置取消标志，阻止所有自动播放
          videoController.isCancelled = true;
          console.log('🚫 [PlaybackStrategyManager] 设置视频控制器取消标志');
          
          // 🔥 移除所有 canplay 监听器，防止自动播放
          const videoElement = videoController.getVideoElement();
          if (videoElement && videoController.currentCanPlayHandlers) {
            console.log('🚫 [PlaybackStrategyManager] 移除视频 canplay 监听器，数量:', videoController.currentCanPlayHandlers.length);
            videoController.currentCanPlayHandlers.forEach(handler => {
              try {
                videoElement.removeEventListener('canplay', handler);
              } catch (error) {
                console.warn('⚠️ [PlaybackStrategyManager] 移除 canplay 监听器时出错:', error);
              }
            });
            videoController.currentCanPlayHandlers = [];
          }
          
          // 🔥 清除 oncanplay 属性（用于 CDN 播放）
          if (videoElement) {
            try {
              videoElement.oncanplay = null;
            } catch (error) {
              console.warn('⚠️ [PlaybackStrategyManager] 清除 oncanplay 时出错:', error);
            }
          }
          
          // 停止视频元素加载（如果存在）
          if (videoElement) {
            try {
              videoElement.pause();
              videoElement.currentTime = 0;
              videoElement.src = '';
              videoElement.load(); // 重置加载状态
              console.log('🚫 [PlaybackStrategyManager] 已停止视频元素加载');
            } catch (error) {
              console.warn('⚠️ [PlaybackStrategyManager] 停止视频元素时出错:', error);
            }
          }
          
          // 清理视频相关的定时器
          if (videoController.streamingTimeout) {
            clearTimeout(videoController.streamingTimeout);
            videoController.streamingTimeout = null;
          }
          
          // 重置加载状态
          videoController.isVideoLoading = false;
          videoController.isVideoCaching = false;
        }
      }
      
      console.log('✅ [PlaybackStrategyManager] 已取消当前策略的加载操作');
    } catch (error) {
      console.error('❌ [PlaybackStrategyManager] 取消加载操作时出错:', error);
      // 不抛出错误，继续执行清理流程
    }
  }
  
  /**
   * 等待清理完成的辅助方法
   * @returns {Promise<void>}
   */
  async waitForCleanupComplete() {
    // 等待一小段时间，确保所有异步清理操作完成
    await new Promise(resolve => setTimeout(resolve, 50));
    
    // 可以添加更复杂的等待逻辑，比如检查策略是否有清理完成的回调
  }
  
  /**
   * 处理自动播放下一首（播放结束时调用）
   */
  async handleAutoNext() {
    const playMode = this.playbackStore.gridFullscreenSingleRepeat
      ? 'single'
      : this.playbackStore.playMode;
    const playlist = this.playbackStore.playlist;
    const isSingleSongPlaylist = playlist && playlist.length === 1;
    const currentSong = this.playbackStore.currentSong;
    const currentIndex = this.playbackStore.currentSongIndex;
    
    console.log('🎵 [PlaybackStrategyManager] 播放结束，当前播放模式:', playMode, '播放列表长度:', playlist?.length || 0, '是否单首歌曲播放列表:', isSingleSongPlaylist);
    console.log('🔁 [AUTO-NEXT TRACE] handleAutoNext 播放上下文快照', {
      currentSongId: currentSong?.id,
      currentSongTitle: currentSong?.title,
      currentSongIndex: currentIndex,
      playMode,
      playlistLength: playlist?.length || 0,
      playlistPreview: (playlist || []).slice(0, 5).map(song => ({
        id: song?.id,
        title: song?.title
      }))
    });
    if ((playMode === 'list' || playMode === 'random') && (playlist?.length || 0) <= 1) {
      console.warn('⚠️ [AUTO-NEXT TRACE] 当前为列表/随机模式，但策略层看到的播放列表长度 <= 1，这通常意味着播放列表同步异常');
    }
    
    if (this.currentStrategy?._userInitiatedPause) {
      console.log('🔁 [AUTO-NEXT TRACE] 用户已暂停，跳过自动续播');
      this.shouldAutoPlay = false;
      return;
    }

    // 🔥 修复：单曲循环模式或列表只有一首歌时，直接重新播放当前歌曲，不需要切换策略
    if (playMode === 'single' || isSingleSongPlaylist) {
      if (isSingleSongPlaylist && playMode === 'list') {
        console.log('🎵 [PlaybackStrategyManager] 列表循环模式但只有一首歌曲 - 循环播放当前歌曲');
      } else {
        console.log('🎵 [PlaybackStrategyManager] 单曲循环模式 - 重新播放当前歌曲');
      }
      try {
        // 重置播放位置到开始
        if (this.currentStrategy && typeof this.currentStrategy.seek === 'function') {
          await this.currentStrategy.seek(0);
        } else if (this.currentStrategy && typeof this.currentStrategy.setCurrentTime === 'function') {
          this.currentStrategy.setCurrentTime(0, { userInitiated: false });
          this.playbackStore.setCurrentTime(0);
        } else {
          this.playbackStore.setCurrentTime(0);
        }
        // 🔥 关键修复：强制设置自动播放标志，确保循环播放
        this.shouldAutoPlay = true;
        // 重新播放
        await this.play();
        console.log('🎵 [PlaybackStrategyManager] 单曲循环 - 已重新播放当前歌曲');
      } catch (error) {
        console.error('❌ [PlaybackStrategyManager] 单曲循环重新播放失败:', error);
        this.playbackStore.setError(error);
      }
      return;
    }
    
    // 其他播放模式（列表循环、随机播放）：切换下一首歌曲
    try {
      // 播放结束触发的自动下一曲必须强制自动播放
      await this.nextSong({ forceAutoPlay: true, reason: 'ended' });
      // 切歌成功，重置连续失败计数
      this._autoNextFailCount = 0;
    } catch (error) {
      console.error('❌ [PlaybackStrategyManager] 自动播放下一首失败:', error);
      // 🔥 单首加载失败（常见于阿里云盘直链 403 / 下载限流）时，不应卡死整条播放链。
      // switchStrategy 失败前已 setCurrentSong 为该首，故再次 handleAutoNext 会自然跳到下一首。
      // 错峰后自动跳过继续尝试；连续失败超过上限才停止并报错，避免死循环。
      const MAX_AUTO_SKIP = 3;
      this._autoNextFailCount = (this._autoNextFailCount || 0) + 1;
      if (this._autoNextFailCount <= MAX_AUTO_SKIP) {
        console.warn(`⏭️ [PlaybackStrategyManager] 第 ${this._autoNextFailCount} 次自动切歌失败，错峰后跳过并尝试下一首`);
        setTimeout(() => {
          this.handleAutoNext().catch(e => {
            console.error('❌ [PlaybackStrategyManager] 跳过后继续切歌仍失败:', e);
          });
        }, 1000 + Math.floor(Math.random() * 600));
      } else {
        console.error('❌ [PlaybackStrategyManager] 连续多首自动切歌失败，停止自动播放');
        this._autoNextFailCount = 0;
        this.playbackStore.setError(error);
      }
    }
  }
  
  /**
   * 播放下一首歌曲
   * 根据播放模式决定下一首是什么
   * @returns {Promise<void>}
   */
  async nextSong(options = {}) {
    const { forceAutoPlay = false, reason = 'manual' } = options;
    // 🔥 修复：确保获取最新的响应式播放列表值
    // 在 Pinia store 中，直接访问 ref 会自动解包，但为了确保获取最新值，
    // 我们应该直接访问 store 的属性，而不是先赋值给局部变量
    // 但为了代码可读性，我们仍然使用局部变量，但添加调试日志确保值是最新的
    const playlist = this.playbackStore.playlist;
    const currentSong = this.playbackStore.currentSong;
    const playMode = this.playbackStore.playMode;
    
    // 🔥 添加详细调试日志
    console.log('🎵 [PlaybackStrategyManager] nextSong 被调用，当前状态:', {
      playlistLength: playlist?.length || 0,
      playlist: playlist,
      currentSong: currentSong?.title || 'null',
      playMode: playMode,
      currentSongIndex: this.playbackStore.currentSongIndex,
      forceAutoPlay,
      reason
    });
    
    if (!playlist || playlist.length === 0) {
      console.warn('⚠️ [PlaybackStrategyManager] 播放列表为空，无法播放下一首', {
        playlist: playlist,
        playlistIsArray: Array.isArray(playlist),
        storePlaylist: this.playbackStore.playlist,
        storePlaylistLength: this.playbackStore.playlist?.length || 0
      });
      return;
    }
    
    if (!currentSong) {
      console.warn('⚠️ [PlaybackStrategyManager] 当前没有播放歌曲，无法播放下一首');
      return;
    }
    
    // 获取当前歌曲在播放列表中的索引
    const currentIndex = this.playbackStore.currentSongIndex;
    console.log('🔁 [AUTO-NEXT TRACE] nextSong 索引计算结果', {
      currentIndex,
      playMode,
      playlistLength: playlist.length,
      currentSongId: currentSong?.id,
      currentSongTitle: currentSong?.title
    });
    
    let nextSong = null;
    
    switch (playMode) {
      case 'single':
        // 单曲循环：重新播放当前歌曲
        nextSong = currentSong;
        console.log('🎵 [PlaybackStrategyManager] 单曲循环模式 - 重新播放当前歌曲:', nextSong.title);
        break;
        
      case 'random':
        // 随机播放：随机选择一首歌曲
        nextSong = this.getRandomNextSong(playlist, currentIndex);
        if (nextSong) {
          console.log('🎵 [PlaybackStrategyManager] 随机播放模式 - 随机选择歌曲:', nextSong.title);
        }
        break;
        
      case 'list':
      default:
        // 列表循环：按顺序播放，最后一首后回到第一首
        if (currentIndex < playlist.length - 1) {
          nextSong = playlist[currentIndex + 1];
          console.log('🎵 [PlaybackStrategyManager] 列表循环模式 - 播放下一首歌曲:', nextSong.title);
        } else {
          nextSong = playlist[0];
          console.log('🎵 [PlaybackStrategyManager] 列表循环模式 - 播放列表结束，回到第一首:', nextSong.title);
        }
        break;
    }
    
    if (nextSong) {
      console.log('🔁 [AUTO-NEXT TRACE] nextSong 已选中下一曲', {
        nextSongId: nextSong?.id,
        nextSongTitle: nextSong?.title,
        forceAutoPlay,
        reason
      });
      // 🔥 关键修复：自动切换下一首时，保存当前播放状态
      // 如果当前正在播放，切换后应该继续播放
      const wasPlaying = this.playbackStore.isPlaying;
      const isSingleSongPlaylist = playlist && playlist.length === 1;
      
      console.log('🎵 [PlaybackStrategyManager] 自动切换到下一首，当前播放状态:', wasPlaying, '是否单首歌曲播放列表:', isSingleSongPlaylist, '是否强制自动播放:', forceAutoPlay);
      
      // 🔥 关键修复：自动切换下一首时，设置自动播放标志
      // 如果列表只有一首歌，强制设置 shouldAutoPlay = true，确保循环播放
      // 否则，如果之前正在播放，则自动播放下一首
      if (isSingleSongPlaylist || forceAutoPlay) {
        console.log('🎵 [PlaybackStrategyManager] 设置自动播放标志为 true', {
          isSingleSongPlaylist,
          forceAutoPlay,
          reason
        });
        this.shouldAutoPlay = true;
      } else {
        console.log('🎵 [PlaybackStrategyManager] 自动切换到下一首，设置自动播放标志');
        this.shouldAutoPlay = wasPlaying; // 如果之前正在播放，则自动播放下一首
      }
      
      // 切换策略（canplay 事件会自动播放，如果 shouldAutoPlay 为 true）
      await this.switchStrategy(nextSong);
    } else {
      console.warn('⚠️ [PlaybackStrategyManager] 无法获取下一首歌曲', {
        currentIndex,
        playMode,
        playlistLength: playlist?.length || 0,
        currentSongId: currentSong?.id,
        currentSongTitle: currentSong?.title
      });
    }
  }
  
  /**
   * 播放上一首歌曲
   * 根据播放模式决定上一首是什么
   * @returns {Promise<void>}
   */
  async prevSong() {
    const playlist = this.playbackStore.playlist;
    const currentSong = this.playbackStore.currentSong;
    const playMode = this.playbackStore.playMode;
    
    if (!playlist || playlist.length === 0) {
      console.warn('⚠️ [PlaybackStrategyManager] 播放列表为空，无法播放上一首');
      return;
    }
    
    if (!currentSong) {
      console.warn('⚠️ [PlaybackStrategyManager] 当前没有播放歌曲，无法播放上一首');
      return;
    }
    
    // 获取当前歌曲在播放列表中的索引
    const currentIndex = this.playbackStore.currentSongIndex;
    
    let prevSong = null;
    
    switch (playMode) {
      case 'single':
        // 单曲循环：重新播放当前歌曲
        prevSong = currentSong;
        console.log('🎵 [PlaybackStrategyManager] 单曲循环模式 - 重新播放当前歌曲:', prevSong.title);
        break;
        
      case 'random':
        // 随机播放：随机选择一首歌曲（上一首和下一首在随机模式下相同逻辑）
        prevSong = this.getRandomNextSong(playlist, currentIndex);
        if (prevSong) {
          console.log('🎵 [PlaybackStrategyManager] 随机播放模式 - 随机选择歌曲:', prevSong.title);
        }
        break;
        
      case 'list':
      default:
        // 列表循环：按顺序播放，第一首前回到最后一首
        if (currentIndex > 0) {
          prevSong = playlist[currentIndex - 1];
          console.log('🎵 [PlaybackStrategyManager] 列表循环模式 - 播放上一首歌曲:', prevSong.title);
        } else {
          prevSong = playlist[playlist.length - 1];
          console.log('🎵 [PlaybackStrategyManager] 列表循环模式 - 播放列表开始，回到最后一首:', prevSong.title);
        }
        break;
    }
    
    if (prevSong) {
      // 🔥 关键修复：自动切换上一首时，设置自动播放标志
      console.log('🎵 [PlaybackStrategyManager] 自动切换到上一首，设置自动播放标志');
      this.shouldAutoPlay = true;
      
      // 切换策略并播放（canplay 事件会自动播放，不需要手动调用 play()）
      await this.switchStrategy(prevSong);
      // 🔥 移除直接调用 play()，让 canplay 事件处理自动播放，避免重复触发
      // await this.play(); // 已移除，由 canplay 事件自动处理
    } else {
      console.warn('⚠️ [PlaybackStrategyManager] 无法获取上一首歌曲');
    }
  }
  
  /**
   * 获取随机下一首歌曲（用于随机播放模式）
   * @param {Array} playlist - 播放列表
   * @param {number} currentIndex - 当前歌曲索引
   * @returns {Object|null} 下一首歌曲对象
   */
  getRandomNextSong(playlist, currentIndex) {
    if (playlist.length === 0) return null;
    
    // 如果播放历史记录长度等于播放列表长度，说明所有歌曲都播放过了
    // 清空播放历史，重新开始随机播放
    if (this.randomPlayHistory.length >= playlist.length) {
      console.log('🎲 [PlaybackStrategyManager] 所有歌曲都已播放过，清空播放历史重新开始');
      this.randomPlayHistory = [];
    }
    
    // 获取还未播放过的歌曲索引
    const unplayedIndices = [];
    for (let i = 0; i < playlist.length; i++) {
      if (!this.randomPlayHistory.includes(i)) {
        unplayedIndices.push(i);
      }
    }
    
    // 从未播放的歌曲中随机选择一首
    const randomIndex = unplayedIndices[Math.floor(Math.random() * unplayedIndices.length)];
    
    if (randomIndex === undefined) {
      console.warn('⚠️ [PlaybackStrategyManager] 无法获取随机歌曲索引');
      return null;
    }
    
    const randomSong = playlist[randomIndex];
    
    // 记录到播放历史（但排除当前歌曲，避免立即重复）
    if (randomIndex !== currentIndex && !this.randomPlayHistory.includes(randomIndex)) {
      this.randomPlayHistory.push(randomIndex);
      console.log('🎲 [PlaybackStrategyManager] 更新随机播放历史:', this.randomPlayHistory);
    }
    
    return randomSong;
  }
  
  clearPauseConfirmTimer() {
    if (this._pauseConfirmTimer) {
      clearTimeout(this._pauseConfirmTimer);
      this._pauseConfirmTimer = null;
    }
  }

  /**
   * 播放
   * @returns {Promise<void>}
   */
  async play() {
    if (!this.currentStrategy) {
      console.warn('⚠️ [PlaybackStrategyManager] 没有当前策略，无法播放');
      return;
    }
    
    try {
      this.clearPauseConfirmTimer();
      // 用户/系统恢复播放时清掉暂停闩，避免 playing 事件被当成「用户已暂停」又 pause 回去
      this.currentStrategy._userInitiatedPause = false;
      // 🔥 修复：不在这里设置播放状态，等待策略的 'playing' 事件
      // 'playing' 事件表示音频真正开始播放，用户能听到声音
      // 这比提前设置状态更准确
      console.log('🎵 [PlaybackStrategyManager] 调用策略 play()，等待 playing 事件更新状态');
      
      await this.currentStrategy.play();
      // 注意：play() 成功后，策略会触发 'play' 事件（调用 play() 后）
      // 然后音频真正开始播放时会触发 'playing' 事件
      // 我们已经在 bindStrategyEvents 中监听 'playing' 事件并更新状态了
      console.log('🎵 [PlaybackStrategyManager] 策略 play() 调用成功，等待 playing 事件');
    } catch (error) {
      console.error('❌ [PlaybackStrategyManager] 播放失败:', error);
      // 播放失败时，确保播放状态为 false（如果之前不是 false）
      this.playbackStore.setPlaying(false);
      this.playbackStore.setError(error);
      throw error;
    }
  }
  
  /**
   * 暂停
   */
  pause() {
    if (!this.currentStrategy) {
      console.warn('⚠️ [PlaybackStrategyManager] 没有当前策略，无法暂停');
      return;
    }
    
    this.currentStrategy.pause();
    // pause() 后，策略会触发 'pause' 事件并更新状态
  }
  
  /**
   * 停止
   */
  stop() {
    if (!this.currentStrategy) {
      console.warn('⚠️ [PlaybackStrategyManager] 没有当前策略，无法停止');
      return;
    }
    
    this.currentStrategy.stop();
    this.playbackStore.setPlaying(false);
    this.playbackStore.setCurrentTime(0);
  }
  
  /**
   * 设置播放时间
   * @param {number} time - 播放时间（秒）
   */
  setCurrentTime(time) {
    if (!this.currentStrategy) {
      return;
    }
    
    this.currentStrategy.setCurrentTime(time);
    this.playbackStore.setCurrentTime(time);
  }
  
  /**
   * 获取当前播放时间
   * @returns {number}
   */
  getCurrentTime() {
    if (!this.currentStrategy) {
      return 0;
    }
    try {
      return this.currentStrategy.getCurrentTime?.() ?? 0;
    } catch (err) {
      console.warn('🎵 [PlaybackStrategyManager] getCurrentTime 失败:', err);
      return this.playbackStore?.currentTime ?? 0;
    }
  }
  
  /**
   * 获取总时长
   * @returns {number}
   */
  getDuration() {
    if (!this.currentStrategy) {
      return 0;
    }
    return this.currentStrategy.getDuration();
  }
  
  /**
   * 是否正在播放
   * @returns {boolean}
   */
  isPlaying() {
    if (!this.currentStrategy) {
      return false;
    }
    return this.currentStrategy.isPlaying();
  }
  
  /**
   * 获取当前策略实例
   * @returns {PlayStrategy|null}
   */
  getCurrentStrategy() {
    return this.currentStrategy;
  }
  
  /**
   * 获取视频元素（仅视频策略有效）
   * @returns {HTMLVideoElement|null}
   */
  getVideoElement() {
    if (!this.currentStrategy || !this.currentStrategy.getVideoElement) {
      return null;
    }
    return this.currentStrategy.getVideoElement();
  }
  
  /**
   * 清理资源（组件卸载时调用）
   */
  cleanup() {
    console.log('🧹 [PlaybackStrategyManager] 清理策略管理器');
    
    // 清理当前策略
    if (this.currentStrategy) {
      try {
        this.currentStrategy.stop();
        this.currentStrategy.cleanup();
      } catch (error) {
        console.error('❌ [PlaybackStrategyManager] 清理策略失败:', error);
      }
      this.currentStrategy = null;
    }
    
    // 清空随机播放历史
    this.randomPlayHistory = [];
    
    // 重置全局状态
    this.playbackStore.reset();
    
    console.log('✅ [PlaybackStrategyManager] 策略管理器清理完成');
  }
  
  /**
   * 清空随机播放历史（切换播放模式时调用）
   */
  clearRandomPlayHistory() {
    this.randomPlayHistory = [];
    console.log('🎲 [PlaybackStrategyManager] 随机播放历史已清空');
  }
}

// 导出单例（推荐使用单例，确保全局只有一个管理器）
let strategyManagerInstance = null;

export function getPlaybackStrategyManager() {
  if (!strategyManagerInstance) {
    strategyManagerInstance = new PlaybackStrategyManager();
  }
  return strategyManagerInstance;
}
